import { useContext, useEffect, useState } from 'react';
import { useCurrentRoute, useNavigation } from 'react-navi';
import { AuthContext, CommonContext, Game, User } from '@repo-level/core-front';

import { USER_GROUP, groupMember } from 'helpers/user';
import { ModalsContext, ModalType } from 'contexts/modal';
import { usePlayerFieldsStatus } from './usePlayerFieldsStatus';

export const isPlayerFieldsFilled = (
  user: User,
  propertiesToCheck: (keyof User)[]
) => {
  const missingFields: (keyof User)[] = [];

  for (const property of propertiesToCheck) {
    if (!user[property]) {
      missingFields.push(property);
    }
  }

  return {
    isAllFieldsFilled: missingFields.length === 0,
    missingFields,
  };
};

export function useGameRedirect({
  gameProvider,
  gameName,
}: {
  gameProvider?: Game['provider'];
  gameName?: string;
}) {
  const { playerFields } = useContext(CommonContext);
  const { isAuthenticated, user } = useContext(AuthContext);
  const [inProcessRedirect, setInProcessRedirect] = useState(true);
  const { handleModal } = useContext(ModalsContext);

  const { navigate } = useNavigation();
  const {
    url: { pathname },
  } = useCurrentRoute();

  const { missingFields, isAllFieldsFilled } = usePlayerFieldsStatus(
    gameProvider
  );

  useEffect(() => {
    if (!gameProvider || !playerFields || !isAuthenticated) {
      setInProcessRedirect(false);
      return;
    }

    const isLimitedAccess = groupMember(USER_GROUP.LA)(user);

    if (isAllFieldsFilled && !isLimitedAccess) {
      setInProcessRedirect(false);
      return;
    }

    if (missingFields.length) {
      handleModal(ModalType.CompleteProfile, {
        stayInSameWindow: true,
        props: {
          gameProvider,
        },
      })();
      return;
    }

    if (isLimitedAccess) {
      navigate(
        `/profile-custom/${
          isLimitedAccess
            ? `limits${isLimitedAccess ? '?action=limitedAccess' : ''}`
            : `personal?back-url=${encodeURIComponent(pathname)}`
        }`
      );
    }
  }, [
    gameProvider,
    isAuthenticated,
    navigate,
    playerFields,
    user,
    pathname,
    handleModal,
    isAllFieldsFilled,
    missingFields.length,
    gameName,
  ]);

  return inProcessRedirect;
}
